import React, { useEffect, useRef, useMemo } from 'react';
import {
	ScreenSpaceEventHandler,
	ScreenSpaceEventType,
	defined,
	Color,
	VerticalOrigin,
} from 'cesium';
import { useQuery } from '@tanstack/react-query';
import { useCesium } from '../../context/CesiumContext';
import { getDamagePolygons } from '@/queries/damage-polygons/get-damage-polygons';
import {
	loadPolygons,
	COLOR_POINT_NORMAL,
	COLOR_POINT_SELECTED,
	COLOR_POLYGON_NORMAL,
	COLOR_POLYGON_SELECTED,
	COLOR_UPLOAD_SELECTED,
	POLYGON_ALPHA_NORMAL,
	POLYGON_ALPHA_SELECTED,
	createMapPinIcon,
} from '@/helper/cesiumHelper';
import { toast } from 'sonner';
// Module-level translate, not the hook: `t` in the click-handler effect deps would
// re-run loadPolygons on a language switch (entities cleared and re-added, selection lost).
import { t as translate } from '@/i18n';

// Green color to indicate "Ready for Upload"
type UploadTarget = { damageId: string; inspectionId?: string };

type DamagesLoaderProps = {
	assetId: string;
	inspectionId?: string;
	damageId?: string;
	onDataLoaded?: (polygons: any[]) => void;
	isUploadMode: boolean;
	selectedUploadItems: UploadTarget[];
	onUploadSelectionChange: (items: UploadTarget[]) => void;
};

const DamagesLoader: React.FC<DamagesLoaderProps> = ({
	assetId,
	inspectionId,
	damageId,
	onDataLoaded,
	isUploadMode,
	selectedUploadItems,
	onUploadSelectionChange,
}) => {
	const normalMaterial = useMemo(
		() => Color.fromCssColorString(COLOR_POLYGON_NORMAL).withAlpha(POLYGON_ALPHA_NORMAL),
		[]
	);

	const selectedMaterial = useMemo(
		() => Color.fromCssColorString(COLOR_POLYGON_SELECTED).withAlpha(POLYGON_ALPHA_SELECTED),
		[]
	);

	const uploadSelectedMaterial = useMemo(
		() => Color.fromCssColorString(COLOR_UPLOAD_SELECTED).withAlpha(POLYGON_ALPHA_SELECTED),
		[]
	);

	const { viewer, tilesLoaded, setSelectedDamage, setSelectedDamageEntity, drawMode } = useCesium();
	const selectedEntityId = useRef<string | null>(null);
	const drawModeRef = useRef(drawMode);

	// Refs used inside event listeners
	const isUploadModeRef = useRef(isUploadMode);
	const selectedUploadItemsRef = useRef(selectedUploadItems);

	// Sync refs
	useEffect(() => {
		drawModeRef.current = drawMode;
	}, [drawMode]);

	useEffect(() => {
		isUploadModeRef.current = isUploadMode;
	}, [isUploadMode]);

	useEffect(() => {
		selectedUploadItemsRef.current = selectedUploadItems;
	}, [selectedUploadItems]);

	const { data: polygonData, isLoading } = useQuery({
		queryKey: ['damage-polygons', assetId],
		queryFn: () => getDamagePolygons({ assetId, limit: 100, offset: 0 }),
		enabled: !!assetId,
	});

	const polygonsToLoad = useMemo(() => polygonData?.polygons ?? [], [polygonData]);

	useEffect(() => {
		if (onDataLoaded && polygonsToLoad.length > 0) {
			onDataLoaded(polygonsToLoad);
		}
	}, [polygonsToLoad, onDataLoaded]);

	// Highlight selected items during Upload Mode
	useEffect(() => {
		if (!viewer || !isUploadMode) return;

		const selectedIds = selectedUploadItems.map((item) => item.damageId);

		const entities = viewer.entities.values;
		entities.forEach((entity: any) => {
			if (entity.type === 'DamagePolygon' || entity.type === 'DamagePoint') {
				const entityDamageId = entity.properties?.damageId?.getValue();

				const isSelected = selectedIds.includes(entityDamageId);

				if (entity.polygon) {
					entity.polygon.material = isSelected ? uploadSelectedMaterial : normalMaterial;
					entity.polygon.outlineColor = isSelected
						? Color.fromCssColorString(COLOR_UPLOAD_SELECTED)
						: Color.BLACK;
					entity.polygon.outlineWidth = isSelected ? 4 : 2;
				}

				if (entity.type === 'DamagePoint' && entity.billboard) {
					entity.billboard.image = isSelected
						? createMapPinIcon(COLOR_UPLOAD_SELECTED)
						: createMapPinIcon(COLOR_POINT_NORMAL);
					entity.billboard.scale = isSelected ? 1.8 : 1.5;
				}
			}
		});
	}, [selectedUploadItems, isUploadMode, viewer, uploadSelectedMaterial, normalMaterial]);

	// Restore colors when exiting Upload Mode
	useEffect(() => {
		if (!isUploadMode && viewer) {
			const entities = viewer.entities.values;
			entities.forEach((entity: any) => {
				if (
					(entity.type === 'DamagePolygon' || entity.type === 'DamagePoint') &&
					entity.id !== selectedEntityId.current // Don't reset if it's the currently edited item
				) {
					if (entity.polygon) {
						entity.polygon.material = normalMaterial;
						entity.polygon.outlineColor = Color.BLACK;
						entity.polygon.outlineWidth = 2;
					}

					if (entity.type === 'DamagePoint' && entity.billboard) {
						entity.billboard.image = createMapPinIcon(COLOR_POINT_NORMAL);
						entity.billboard.scale = 1.5;
					}
				}
			});
		}
	}, [isUploadMode, viewer, normalMaterial]);

	// CLICK HANDLER
	useEffect(() => {
		if (!viewer || !tilesLoaded || isLoading) return;

		loadPolygons(viewer, polygonsToLoad);

		const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);

		handler.setInputAction((movement: any) => {
			if (drawModeRef.current === 'EDIT' || drawModeRef.current === 'INSERT') {
				return;
			}

			const pickedObject = viewer.scene.pick(movement.position);
			let clickedDamageEntity: any | null = null;

			if (defined(pickedObject) && pickedObject.id) {
				const entity = pickedObject.id as any;

				// Updated Name to match the new Attachment Icon entity name
				if (
					entity.type === 'DamagePolygon' ||
					entity.type === 'DamagePoint' ||
					entity.name === 'damage-attachment-icon'
				) {
					// If they clicked the icon directly, find the master entity it represents
					if (entity.name === 'damage-attachment-icon') {
						const dmgId = entity.properties?.damageId?.getValue();
						clickedDamageEntity = viewer.entities.values.find(
							(e: any) =>
								(e.type === 'DamagePolygon' || e.type === 'DamagePoint') &&
								e.properties?.damageId?.getValue() === dmgId
						);
					} else {
						clickedDamageEntity = entity;
					}
				}
			}

			// ==========================================
			// MODE A: UPLOAD (MULTI-SELECT LOGIC)
			// ==========================================
			if (isUploadModeRef.current) {
				if (clickedDamageEntity) {
					const currentItems = selectedUploadItemsRef.current;
					const clickedId = clickedDamageEntity.properties?.damageId?.getValue() ?? null;
					const clickedDamageId = clickedDamageEntity.properties?.damageId?.getValue();
					const clickedInspectionId = clickedDamageEntity.properties?.inspectionId?.getValue();

					if (!clickedDamageId) {
						toast.error(translate('damages.toast.notLinkedIgnored'));
						return;
					} else {
						toast.success(translate('damages.toast.selectionToggled', { id: clickedId }));
					}
					const exists = currentItems.some((item) => item.damageId === clickedDamageId);

					let newItems = [];

					if (exists) {
						newItems = currentItems.filter((item) => item.damageId !== clickedDamageId);
					} else {
						newItems = [
							...currentItems,
							{ damageId: clickedDamageId, inspectionId: clickedInspectionId },
						];
					}
					onUploadSelectionChange(newItems);
				}
				// Return early so standard selection doesn't run
				return;
			}

			// ==========================================
			// MODE B: STANDARD (SINGLE SELECT LOGIC)
			// ==========================================

			// Reset Previous
			if (
				selectedEntityId.current &&
				(!clickedDamageEntity || selectedEntityId.current !== clickedDamageEntity.id)
			) {
				const old: any = viewer.entities.getById(selectedEntityId.current);
				if (old) {
					if (old.polygon) {
						old.polygon.material = normalMaterial;
						old.polygon.outlineColor = Color.BLACK;
						old.polygon.outlineWidth = 2;
					}

					if (old.type === 'DamagePoint' && old.billboard) {
						old.billboard.image = createMapPinIcon(COLOR_POINT_NORMAL);
						old.billboard.scale = 1.5;
						old.billboard.verticalOrigin = VerticalOrigin.BOTTOM;
					}
				}
				selectedEntityId.current = null;
			}

			// Select New
			if (clickedDamageEntity) {
				if (clickedDamageEntity.polygon) {
					clickedDamageEntity.polygon.material = selectedMaterial;
					clickedDamageEntity.polygon.outlineColor =
						Color.fromCssColorString(COLOR_POLYGON_SELECTED);
					clickedDamageEntity.polygon.outlineWidth = 12;
				}

				if (clickedDamageEntity.type === 'DamagePoint' && clickedDamageEntity.billboard) {
					clickedDamageEntity.billboard.image = createMapPinIcon(COLOR_POINT_SELECTED);
					clickedDamageEntity.billboard.scale = 2.0;
					clickedDamageEntity.billboard.verticalOrigin = VerticalOrigin.BOTTOM;
				}

				selectedEntityId.current = clickedDamageEntity.id;
				const rawCoords =
					clickedDamageEntity.properties?.rawPositions?.getValue() ??
					clickedDamageEntity.properties?.rawPosition?.getValue();

				setSelectedDamage(rawCoords);
				setSelectedDamageEntity(clickedDamageEntity);
			} else {
				setSelectedDamage(null);
				setSelectedDamageEntity(null);
			}
		}, ScreenSpaceEventType.LEFT_CLICK);

		return () => {
			handler.destroy();
		};
	}, [viewer, tilesLoaded, polygonsToLoad, isLoading, normalMaterial, selectedMaterial, isUploadMode]);

	return null;
};

export default DamagesLoader;
